import { useMemo } from 'react'
import { isArray, isEmpty } from 'lodash-es'
import { COMPANY_REQUIRED_DOCUMENTS, INDIVIDUAL_REQUIRED_DOCUMENTS } from '../helpers/constants'
import {
  CustomerDocument,
  DocumentType,
  MergedDocument,
  RequiredButNotUploadedDocument,
  RequiredDocument,
  UploadedCustomerDocument,
} from '../helpers/types'

const getRequiredType = (type?: DocumentType): string => {
  if (!type) return ''
  if (isArray(type)) return type?.[0]
  return type
}

const initialRequiredDocuments = (
  reqDoc: RequiredDocument,
  isCompany: boolean
): CustomerDocument => {
  const { type, subType = '' } = reqDoc || {}
  const requiredType = getRequiredType(type)
  const resolvedSubType = isCompany ? subType : subType || requiredType

  return {
    reference: '',
    externalReference: '',
    type: requiredType,
    name: requiredType,
    description: '',
    status: 'PENDING',
    subType: resolvedSubType,
  }
}

const listRequiredDocuments = (isCompany: boolean) =>
  isCompany ? COMPANY_REQUIRED_DOCUMENTS : INDIVIDUAL_REQUIRED_DOCUMENTS

const isOtherStandalone = (reqDoc: RequiredDocument) => {
  const { type } = reqDoc || {}
  return type === 'OTHER'
}

const findMatchingReceivedDoc = (
  reqDoc: RequiredDocument,
  isCompany: boolean,
  receivedDocsMap: Map<string, CustomerDocument>
): CustomerDocument | undefined => {
  const { type, subType = '' } = reqDoc || {}
  const typesArray = isArray(type) ? type : [type]
  const keys = (isCompany ? [subType] : typesArray)?.filter(Boolean)

  const matchedDocumentKey = keys?.find(key => receivedDocsMap?.has(key))
  return matchedDocumentKey ? receivedDocsMap?.get(matchedDocumentKey) : undefined
}

const buildNotUploadedRequired = (isCompany: boolean): RequiredButNotUploadedDocument[] =>
  listRequiredDocuments(isCompany)
    .filter(reqDoc => !isOtherStandalone(reqDoc))
    .map(reqDoc => ({ ...initialRequiredDocuments(reqDoc, isCompany), isUploaded: false }))

const countBaseRequired = (isCompany: boolean): number =>
  listRequiredDocuments(isCompany)?.filter(({ type }) => type !== 'OTHER')?.length

const getReceivedDocuments = (receivedDocuments: CustomerDocument[], isCompany: boolean) => {
  const receivedDocsMap = new Map<string, CustomerDocument>()
  const otherUploadedDocs: CustomerDocument[] = []

  for (const doc of receivedDocuments) {
    const documentType = isCompany ? doc?.subType : doc?.type
    if (documentType) {
      receivedDocsMap?.set(documentType, doc)
      continue
    }
    if (doc?.type === 'OTHER') {
      otherUploadedDocs?.push(doc)
    }
  }

  return { receivedDocsMap, otherUploadedDocs }
}

const getOtherDocuments = (
  receivedDocsMap: Map<string, CustomerDocument>,
  otherUploadedDocs: CustomerDocument[],
  isCompany: boolean
) => {
  const uploadedDocs: UploadedCustomerDocument[] = []
  const requiredDocs: RequiredButNotUploadedDocument[] = []

  const requiredDocuments = listRequiredDocuments(isCompany)

  for (const reqDoc of requiredDocuments) {
    const match = findMatchingReceivedDoc(reqDoc, isCompany, receivedDocsMap)

    if (match) {
      uploadedDocs?.push({ ...match, isUploaded: true })
      continue
    }
    if (isOtherStandalone(reqDoc)) continue
    requiredDocs?.push({ ...initialRequiredDocuments(reqDoc, isCompany), isUploaded: false })
  }

  if (!isEmpty(otherUploadedDocs)) {
    uploadedDocs?.push(
      ...otherUploadedDocs.map((doc): UploadedCustomerDocument => ({ ...doc, isUploaded: true }))
    )
  }

  return { uploadedDocs, requiredDocs }
}

export const useMergedDocuments = (receivedDocuments: CustomerDocument[], isCompany: boolean) => {
  return useMemo(() => {
    if (!receivedDocuments || isEmpty(receivedDocuments)) {
      const mergedDocuments: RequiredButNotUploadedDocument[] = buildNotUploadedRequired(isCompany)
      const requiredCount = listRequiredDocuments(isCompany)?.filter(
        reqDoc => reqDoc?.type !== 'OTHER'
      ).length
      return { mergedDocuments, uploadedCount: 0, requiredCount }
    }
    const { receivedDocsMap, otherUploadedDocs } = getReceivedDocuments(
      receivedDocuments,
      isCompany
    )
    const { uploadedDocs, requiredDocs } = getOtherDocuments(
      receivedDocsMap,
      otherUploadedDocs,
      isCompany
    )

    const mergedDocuments: MergedDocument[] = [...uploadedDocs, ...requiredDocs]

    const uploadedCount = uploadedDocs?.length
    const baseRequiredTotal = countBaseRequired(isCompany)

    const requiredCount = baseRequiredTotal + (otherUploadedDocs?.length > 0 ? 1 : 0)

    return { mergedDocuments, uploadedCount, requiredCount }
  }, [receivedDocuments, isCompany])
}
